let worker = null;
let workerPromise = null;
let progressHandler = null;

const OCR_STARTUP_TIMEOUT_MS = 45000;
const TESSERACT_LANG_PATH = 'https://cdn.jsdelivr.net/npm/@tesseract.js-data/eng/4.0.0_best_int';

const publicPath = (path) => `${process.env.PUBLIC_URL || ''}${path}`;

const TESSERACT_PATHS = {
  worker: publicPath('/vendor/tesseract/worker.min.js'),
  core: publicPath('/vendor/tesseract-core'),
  coreLoader: publicPath('/vendor/tesseract-core/tesseract-core-lstm.wasm.js'),
  lang: TESSERACT_LANG_PATH,
  langData: `${TESSERACT_LANG_PATH}/eng.traineddata.gz`,
};

const withTimeout = (promise, timeoutMs, message) => new Promise((resolve, reject) => {
  const timeoutId = window.setTimeout(() => {
    reject(new Error(message));
  }, timeoutMs);

  promise
    .then((value) => {
      window.clearTimeout(timeoutId);
      resolve(value);
    })
    .catch((error) => {
      window.clearTimeout(timeoutId);
      reject(error);
    });
});

const reportProgress = (status, progress = null) => {
  if (typeof progressHandler === 'function') {
    progressHandler({ status, progress });
  }
};

const assertAssetAvailable = async ({ label, url }) => {
  reportProgress(`Checking ${label}`, null);

  const response = await fetch(url, {
    method: 'GET',
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`Tesseract asset unavailable: ${label} (${response.status}) at ${url}`);
  }
};

const preflightTesseractAssets = async () => {
  await assertAssetAvailable({ label: 'OCR worker', url: TESSERACT_PATHS.worker });
  await assertAssetAvailable({ label: 'OCR core loader', url: TESSERACT_PATHS.coreLoader });
  await assertAssetAvailable({ label: 'OCR English language data', url: TESSERACT_PATHS.langData });
};

export const createOcrWorker = async (onProgress) => {
  progressHandler = onProgress;

  if (worker) {
    return worker;
  }

  if (!workerPromise) {
    workerPromise = withTimeout(
      preflightTesseractAssets().then(() => {
        reportProgress('Loading Tesseract API', 0.02);
        return import('tesseract.js');
      }).then(({ createWorker }) => {
        reportProgress('Loading local Tesseract worker', 0.08);

        return createWorker('eng', 1, {
          workerPath: TESSERACT_PATHS.worker,
          corePath: TESSERACT_PATHS.core,
          langPath: TESSERACT_PATHS.lang,
          logger: (message) => {
            if (typeof progressHandler === 'function') {
              progressHandler(message);
            }
          },
          errorHandler: (error) => {
            const message = error?.message || 'Tesseract worker error';
            console.error('Tesseract worker error', error);

            if (typeof progressHandler === 'function') {
              progressHandler({
                status: message,
                progress: 0,
              });
            }
          },
        });
      }),
      OCR_STARTUP_TIMEOUT_MS,
      'Tesseract took too long to start. Local OCR assets are available, but browser worker initialization stalled.'
    )
      .then((nextWorker) => {
        reportProgress('Tesseract ready', 1);

        worker = nextWorker;
        return nextWorker;
      })
      .catch((error) => {
        worker = null;
        workerPromise = null;

        if (typeof progressHandler === 'function') {
          progressHandler({
            status: error?.message || 'Failed to start Tesseract',
            progress: 0,
          });
        }

        throw error;
      });
  }

  return workerPromise;
};

export const terminateOcrWorker = async () => {
  progressHandler = null;

  if (!worker && !workerPromise) {
    return;
  }

  const activeWorker = worker || await workerPromise;
  worker = null;
  workerPromise = null;

  if (activeWorker && typeof activeWorker.terminate === 'function') {
    await activeWorker.terminate();
  }
};
